/**
 * Scroll-spy for the product detail sub-navigation. A single component scope
 * wraps both the sticky sub-nav and the content sections so they share state.
 *
 * The active section is the last one whose top has scrolled above the bottom
 * edge of the sticky bar (i.e. the section you have scrolled up to). Nothing
 * is highlighted until the bar reaches the top of the viewport and the first
 * section's top passes beneath it, so the first button does not light up while
 * the hero is still on screen.
 *
 * The target sections must be siblings, not nested — each section's top is
 * read independently, so a section that wraps the others would never relinquish
 * the highlight as you scroll into the ones inside it.
 *
 * Usage:
 *   <div x-data="productSubnav">
 *     <nav class="product-subnav">
 *       <a :class="{ 'active': isActive( 'specs' ) }" href="#specs">…</a>
 *     </nav>
 *     <div id="specs">…</div>
 *     <div id="models">…</div>
 *   </div>
 */

document.addEventListener( 'alpine:init', () => {

	Alpine.data( 'productSubnav', () => ({

		// Section ids in declared (top-to-bottom) order.
		order: [ 'specs', 'models', 'resources', 'certifications' ],

		active  : '',
		bar     : null,
		sections: [],
		ticking : false,

		init() {
			this.bar = this.$el.querySelector( '.product-subnav' );

			this.sections = this.order
				.map( ( id ) => ({ id, el: document.getElementById( id ) }) )
				.filter( ( section ) => null !== section.el );

			this.onScroll = () => {
				if ( this.ticking ) {
					return;
				}

				this.ticking = true;

				window.requestAnimationFrame( () => {
					this.compute();
					this.ticking = false;
				});
			};

			window.addEventListener( 'scroll', this.onScroll, { passive: true });
			window.addEventListener( 'resize', this.onScroll, { passive: true });

			// Publish the bar's height as a transparent runway on the scope so
			// the sticky bar's full height clears before its containing block
			// ends — the bar rolls up cleanly right as the gray section
			// finishes. The bar wraps responsively, so re-measure on resize.
			this.measureBar = () => {
				if ( this.bar ) {
					this.$el.style.setProperty(
						'--product-subnav-height',
						`${ this.bar.offsetHeight }px`
					);
				}
			};

			window.addEventListener( 'resize', this.measureBar, { passive: true });

			this.measureBar();
			this.compute();

			this.$cleanup = () => {
				window.removeEventListener( 'scroll', this.onScroll );
				window.removeEventListener( 'resize', this.onScroll );
				window.removeEventListener( 'resize', this.measureBar );
			};
		},

		compute() {
			if ( ! this.bar ) {
				return;
			}

			const barRect = this.bar.getBoundingClientRect();

			// Nothing is highlighted until the sticky bar reaches the top of
			// the viewport. A 1px epsilon absorbs sub-pixel rounding.
			if ( 1 < barRect.top ) {
				this.active = '';

				return;
			}

			// The bar is pinned. The active section is the last one whose top has
			// scrolled to or above the threshold line.
			//
			// The line must sit at or just below where a clicked link actually
			// lands a section. The CSS scroll-margin-top is the browser's TARGET
			// (scroll-mt-36 → 180px from the viewport top), but measured behaviour
			// shows sections settle ~1.6rem lower than that target once smooth
			// scroll and post-snap layout settle. So the line is the section's own
			// scroll-margin plus that settle slack: a clicked section lands just
			// above the line and activates immediately, no "scroll a little
			// farther" gap. Anchoring to scroll-margin (read live) keeps click
			// target and natural-scroll trigger on the SAME line and tracks the
			// CSS across breakpoints / root font size. The next section sits far
			// below (~17rem), so the slack never reaches it.
			const rootFontSize = parseFloat( getComputedStyle( document.documentElement ).fontSize );
			const scrollMargin = this.sections.length
				? parseFloat( getComputedStyle( this.sections[ 0 ].el ).scrollMarginTop )
				: barRect.bottom;
			const line = scrollMargin + ( rootFontSize * 2.5 );

			let current = this.sections.length ? this.sections[ 0 ].id : '';

			for ( const section of this.sections ) {
				if ( section.el.getBoundingClientRect().top <= line ) {
					current = section.id;
				}
			}

			this.active = current;
		},

		isActive( id ) {
			return id === this.active;
		},

	}) );
});
